import { memo, ReactNode, useEffect } from "react";

export const TEXT_HIGHLIGHT_EVENT = "redo:text-highlight";
const TEXT_HIGHLIGHT_ATTRIBUTE = "data-redo-text-highlight";
const MAX_HIGHLIGHT_TEXT_LENGTH = 100;

interface TextHighlightEventDetail {
  highlightText: string;
  scrollToFirstMatch?: boolean;
}

export function dispatchTextHighlight({
  highlightText,
  scrollToFirstMatch = false,
}: TextHighlightEventDetail): void {
  window.dispatchEvent(
    new CustomEvent<TextHighlightEventDetail>(TEXT_HIGHLIGHT_EVENT, {
      detail: { highlightText, scrollToFirstMatch },
    }),
  );
}

export const TextHighlighterProvider = memo(function TextHighlighterProvider({
  children,
}: {
  children: ReactNode;
}) {
  useEffect(() => {
    let highlightTimeoutId: number | undefined;

    const queueHighlight = ({
      highlightText,
      scrollToFirstMatch,
      delayMs,
      warnOnMissing,
      onFound,
    }: {
      delayMs: number;
      highlightText: string;
      onFound?: () => void;
      scrollToFirstMatch: boolean;
      warnOnMissing: boolean;
    }) => {
      if (highlightTimeoutId !== undefined) {
        window.clearTimeout(highlightTimeoutId);
      }

      clearTextHighlights();

      const sanitizedText = sanitizeHighlightText(highlightText);
      if (!sanitizedText) {
        return;
      }

      highlightTimeoutId = window.setTimeout(() => {
        try {
          const regex = createHighlightRegex(sanitizedText);
          const found = searchAndHighlightText(regex, { scrollToFirstMatch });

          if (found) {
            onFound?.();
            return;
          }

          if (!found && warnOnMissing) {
            console.warn(
              `TextHighlighterProvider: No matches found for pattern "${highlightText}"`,
            );
          }
        } catch (error) {
          console.error("Error highlighting text:", error);
        }
      }, delayMs);
    };

    const checkAndHighlight = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const highlightText = urlParams.get("highlightText");

      if (!highlightText) {
        return;
      }

      queueHighlight({
        delayMs: 1000,
        highlightText,
        onFound: () => {
          urlParams.delete("highlightText");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        },
        scrollToFirstMatch: true,
        warnOnMissing: true,
      });
    };

    const handleTextHighlight = (event: Event) => {
      const { highlightText, scrollToFirstMatch = false } = (
        event as CustomEvent<TextHighlightEventDetail>
      ).detail;

      queueHighlight({
        delayMs: 50,
        highlightText,
        scrollToFirstMatch,
        warnOnMissing: false,
      });
    };

    // Check on mount
    checkAndHighlight();

    // Intercept pushState and replaceState to detect navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      checkAndHighlight();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      checkAndHighlight();
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", checkAndHighlight);
    window.addEventListener(TEXT_HIGHLIGHT_EVENT, handleTextHighlight);

    return () => {
      if (highlightTimeoutId !== undefined) {
        window.clearTimeout(highlightTimeoutId);
      }
      clearTextHighlights();
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", checkAndHighlight);
      window.removeEventListener(TEXT_HIGHLIGHT_EVENT, handleTextHighlight);
    };
  }, []);

  return <>{children}</>;
});

function sanitizeHighlightText(highlightText: string): string {
  return highlightText.trim().slice(0, MAX_HIGHLIGHT_TEXT_LENGTH);
}

function createHighlightRegex(highlightText: string): RegExp {
  const escapedText = highlightText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(escapedText, "gi");
}

function clearTextHighlights(): void {
  for (const highlight of document.querySelectorAll(
    `mark[${TEXT_HIGHLIGHT_ATTRIBUTE}="true"]`,
  )) {
    const parent = highlight.parentNode;
    highlight.replaceWith(document.createTextNode(highlight.textContent ?? ""));
    parent?.normalize();
  }
}

function searchAndHighlightText(
  pattern: RegExp,
  { scrollToFirstMatch }: { scrollToFirstMatch: boolean },
): boolean {
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parent = node.parentElement;

        // Skip script and style elements
        if (
          parent?.tagName === "SCRIPT" ||
          parent?.tagName === "STYLE" ||
          parent?.tagName === "NOSCRIPT"
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        if (parent?.closest(`mark[${TEXT_HIGHLIGHT_ATTRIBUTE}="true"]`)) {
          return NodeFilter.FILTER_REJECT;
        }

        // Skip elements in slide-out panels (fixed/absolute positioned containers)
        let element: HTMLElement | null = parent;
        while (element && element !== document.body) {
          // Skip if element has data-exclude-text-highlight attribute
          if (element.hasAttribute?.("data-exclude-text-highlight")) {
            return NodeFilter.FILTER_REJECT;
          }

          const position = window.getComputedStyle(element).position;
          // Skip if in a fixed/absolute positioned container that's likely a modal/slideout
          if (
            (position === "fixed" || position === "absolute") &&
            element.offsetWidth > 200 // Likely a panel, not just a tooltip
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          element = element.parentElement;
        }

        // Only accept nodes with matching text
        return node.textContent?.match(pattern)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    },
  );

  let firstMatch: HTMLElement | null = null;
  const nodesToProcess: Text[] = [];

  // Collect all text nodes that match
  let node: Node | null;
  while ((node = walker.nextNode())) {
    nodesToProcess.push(node as Text);
  }

  // Process each text node
  for (const textNode of nodesToProcess) {
    const parent = textNode.parentNode;
    if (!parent) {
      continue;
    }

    const text = textNode.textContent || "";
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    // Reset regex state
    pattern.lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        fragment.append(
          document.createTextNode(text.slice(lastIndex, match.index)),
        );
      }

      // Add highlighted match
      const mark = document.createElement("mark");
      mark.setAttribute(TEXT_HIGHLIGHT_ATTRIBUTE, "true");
      mark.style.backgroundColor =
        "var(--redo-colors-background-bg-warning-secondary)";
      mark.style.borderRadius = "var(--redo-corner-xsmall-radius)";
      mark.style.boxDecorationBreak = "clone";
      mark.style.setProperty("-webkit-box-decoration-break", "clone");
      mark.textContent = match[0];
      fragment.append(mark);

      // Store first match for scrolling
      if (!firstMatch) {
        firstMatch = mark;
      }

      lastIndex = pattern.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.append(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }

  // Scroll first match into view
  if (firstMatch && scrollToFirstMatch) {
    firstMatch.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  if (firstMatch) {
    return true;
  }

  return false;
}
